// Basic
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// Libs
import { cn } from '../../../../../common-utils/libraries/tailwind/utils';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Store & Actions
import useSessionStore from '@/app/store/session';
// Components
import HeaderTitle from '../../../../../common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import FooterClose from '@/common-utils/components/renders/modal/components/footers/FooterClose/FooterClose';
import { Button } from '@/common-utils/components/ui/button';
import { Modal, ModalBody, ModalContent } from '../../../../../common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import MerchantContractDomiciliation from '@/common-utils/enums/MerchantContractDomiciliation';
import ICONS from '@/common-utils/enums/icons';
import buildDictionary from '@/common-utils/functions/buildDictionary';
import { findApplicationFromCode } from '../../enums/ItpRegex';
import { useToast } from '@/common-utils/hooks/use-toast';
import { luhnCalculate, luhnValidate } from '@/common-utils/functions/getLuhnKeyFromCode';
// Lang
import { nsContract } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.isCustomDomiciliation
 * @param root0.contractNumber
 * @param root0.applicationCode
 * @returns {JSX} ModalDomiciliation
*/
function ModalDomiciliation({
    isOpen,
    setIsOpen,
    isCustomDomiciliation,
    contractNumber: contractNumberFromProps,
    applicationCode: applicationCodeFromProps
}) {
    // Constants
    const STEPS_NUMBER = 2;
    const applicationListOptions = Object.values(MerchantContractDomiciliation).map((val) => val.application);

    // Store
    const { isSmallDevice } = useSessionStore(s => s.session);

    // State
    const [step, setStep] = useState(isCustomDomiciliation ? 0 : 1);
    const [isApplicationInputAmex, setIsApplicationInputAmex] = useState(false);
    const [isDisabledAction, setIsDisabledAction] = useState(true);

    // Toast
    const { toast } = useToast();

    // Zod Schema
    const formSchema = z.object({
        application: z.enum(applicationListOptions),
        contractNumber: z.string()
            .length(isApplicationInputAmex ? 10 : 7)
            .refine((number) => /^\d*$/.test(number), { message: '' })
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { application: '', contractNumber: '' }
    });

    // Hooks
    useEffect(() => {
        const { application, contractNumber } = form.getValues();
        setIsDisabledAction(!form.formState.isValid ||
            (application === MerchantContractDomiciliation.AMEX.application
                || application === MerchantContractDomiciliation.AMEXCLESS.application)
            && contractNumber.length !== 10);
    }, [form.formState.isValid, form.getValues()]);

    // Functions
    const getContractNumber = () => {
        const { application: applicationFromForm, contractNumber: contractNumberFromForm } = form.getValues();
        const contractNumber = contractNumberFromProps || !contractNumberFromProps && contractNumberFromForm;
        const applicationCode = applicationCodeFromProps || !applicationCodeFromProps && applicationFromForm;
        const isApplicationAmex = applicationCode &&
            applicationCode === MerchantContractDomiciliation.AMEX.application
            || applicationCode === MerchantContractDomiciliation.AMEXCLESS.application;
        if (isApplicationAmex) {
            return contractNumber ? contractNumber.slice(3) : null;
        }
        return contractNumber || null;
    };

    const getEncryptedCode = () => {
        const contractNumber = getContractNumber();
        const { application: applicationFromForm } = form.getValues();
        const application = applicationCodeFromProps ?? applicationFromForm ?? null;
        const applicationToEncrypt = application ? MerchantContractDomiciliation[application] : null;
        const codeWithoutLuhn = applicationToEncrypt && contractNumber ? applicationToEncrypt.startEncryption.concat('', contractNumber) : null;
        const codeWithLuhn = codeWithoutLuhn ? luhnCalculate(codeWithoutLuhn) : null;
        const isLuhnKeyValid = codeWithLuhn && luhnValidate(codeWithLuhn);

        return isLuhnKeyValid ? codeWithLuhn.concat('', applicationToEncrypt.endEncryption) : null;
    };

    const handleCopyText = () => {
        const range = document.createRange();
        range.selectNodeContents(document.getElementById('code'));
        window.getSelection().removeAllRanges(); // clear current selection
        window.getSelection().addRange(range); // to select text
        document.execCommand('copy');
        window.getSelection().removeAllRanges();// to deselect

        toast({
            description: <Entity namespace={nsContract} entity='modal.domiciliation.copy_to_clipboard.success' />,
            variant: 'success'
        });
    };

    const getApplicationLogoAndName = () => {
        const { application: applicationFromForm } = form.getValues();

        if (applicationCodeFromProps) {
            return findApplicationFromCode(applicationCodeFromProps);
        }
        else if (applicationFromForm) {
            return findApplicationFromCode(applicationFromForm);
        }

        return null;
    };

    const getApplicationListOptions = () => {
        return buildDictionary(Object.values(MerchantContractDomiciliation), 'application');
    };

    const handleSelectApplication = (value) => {
        form.setValue('contractNumber', value === MerchantContractDomiciliation.AMEX.application
            || value === MerchantContractDomiciliation.AMEXCLESS.application ? '949' : '');
        setIsApplicationInputAmex(value === MerchantContractDomiciliation.AMEX.application
            || value === MerchantContractDomiciliation.AMEXCLESS.application);
        form.trigger();
    };

    const onClose = () => {
        form.reset();
        setIsOpen(false);
    };

    const applicationData = getApplicationLogoAndName();
    const { application: applicationFromForm, contractNumber: contractNumberFromForm } = form.getValues();

    // Render
    return (
        <Modal open={isOpen} onOpenChange={onClose}>
            <ModalContent className={cn('max-w-lg')}>
                <HeaderTitle title='modal.domiciliation.title' namespace={nsContract} />
                <ModalBody>
                    {step === 0 &&
                        <Form {...form}>
                            <FormField
                                name='application'
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className='select-application'>
                                        <FormLabel required>
                                            <Entity namespace={nsContract} entity='modal.domiciliation.application' />
                                        </FormLabel>
                                        <FormControl>
                                            <Select
                                                {...field}
                                                options={getApplicationListOptions()}
                                                prefixEntity='application.'
                                                namespace={nsContract}
                                                value={field.value}
                                                onChange={(value) => {
                                                    field.onChange(value);
                                                    handleSelectApplication(value);
                                                }}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <div className='mt-4'>
                                <FormField
                                    name='contractNumber'
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem className='addresses-list-input'>
                                            <FormLabel required>
                                                <Entity namespace={nsContract} entity='modal.domiciliation.contract_number' />
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    namespace={nsContract}
                                                    autoComplete='off'
                                                    value={field.value}
                                                    onChange={(value) => {
                                                        field.onChange(value);
                                                    }}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </Form>
                    }
                    {step === 1 &&
                        <div className='text-center space-y-2'>
                            <div>
                                {
                                    applicationCodeFromProps && applicationData
                                        || !applicationCodeFromProps && applicationFromForm ?
                                        <div>
                                            <div>
                                                <img
                                                    className='h-[40px] mx-auto'
                                                    src={applicationData.logo}
                                                    alt={applicationData.name}
                                                    title={applicationData.name} />
                                            </div>
                                            <small>
                                                {<Entity namespace={nsContract} entity={`application.${applicationData.name}`} />}
                                            </small>
                                        </div>
                                        : null
                                }
                                {
                                    contractNumberFromProps || contractNumberFromForm ?
                                        <div className='contract-number'>
                                            <Entity
                                                namespace={nsContract}
                                                entity='modal.domiciliation.title_contract_only'
                                                data={{
                                                    number: contractNumberFromProps || contractNumberFromForm
                                                }}
                                                className='font-bold'
                                            />
                                        </div>
                                        : null
                                }
                            </div>
                            {
                                getEncryptedCode() ?
                                    <div className='space-y-2'>
                                        <div>
                                            <Entity namespace={nsContract} entity='modal.domiciliation.iso_track' />
                                        </div>
                                        <div className={isSmallDevice ? 'code small' : 'code'} id='code'>
                                            {getEncryptedCode()}
                                        </div>
                                        <div className='copy-to-clipboard'>
                                            <Button
                                                variant='outline'
                                                icon={ICONS.FILE_COPY}
                                                entity='modal.domiciliation.copy_to_clipboard.copy'
                                                namespace={nsContract}
                                                onClick={handleCopyText}
                                            />
                                        </div>
                                    </div>
                                    :
                                    <Entity entity='modal.domiciliation.error' />
                            }
                        </div>
                    }
                </ModalBody>
                {isCustomDomiciliation && step === 0 ?
                    <FooterStep
                        step={step}
                        setStep={setStep}
                        stepsNumber={STEPS_NUMBER}
                        disableValidation={isDisabledAction}
                    />
                    : <FooterClose />}
            </ModalContent>
        </Modal>);
}

ModalDomiciliation.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    isCustomDomiciliation: PropTypes.bool,
    contractNumber: PropTypes.string,
    applicationCode: PropTypes.string
};

export default ModalDomiciliation;